import "./transactions.scss";
import * as React from "react";
import { Empty, Popover, Table as AntdTable, Tag, Tooltip } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import {
    EditOutlined,
    ExportOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import moment from "jalali-moment";
import { Badge, UserLink, CopyablePhone, CopyableText } from "components";
import { Card } from "components/card/card";
import { Button, buttonType } from "components/button/button";
import { ColumnSearch } from "components/column-search/column-search";
import { Page } from "components/page/page";
import { BadgeType } from "components/badge/badge";
import { Enum, PaginationList } from "core/domain";
import {
    Transaction,
    TransactionCount,
    TransactionFilterValues,
    TransactionType,
} from "core/domain/transaction/transaction";
import { SortDirection } from "core/domain/order/order";
import { digitSperator, faNumToEnNum } from "core/utils/decimalExtentions";
import { downloadCSV } from "core/utils/export-to-file";
import { getIconPath } from "core/utils/getIconPath";
import { jFormat } from "core/utils/jFormat";
import { getMarketTypes } from "core/repositories/orders";
import {
    getAllTransactions,
    getDepositCurrencyType,
    getTransactionPlatforms,
    getTransactionStates,
    getTransactionTypes,
} from "core/repositories/transaction";
import { TransactionDetailModal } from "./transactio-details/transaction-detail";
import { TransactionEditModal } from "./edit-modal/edit-modal";

const initialFilters: TransactionFilterValues = {
    marketType: { id: -1, title: "نوع ارز" },
    state: { id: -1, title: "همه وضعیت ها" },
    type: { id: -1, title: "همه تراکنش ها" },
    platform: { id: -1, title: "همه پلتفرم ها" },
    depositCurrencyType: { id: -1, title: "نوع واریز" },
    source: { id: -1, title: "همه منابع" },
    sort: SortDirection.DESC,
    startDate: moment()
        .locale("fa")
        .subtract(1, "months")
        .format("jYYYY/jMM/jDD"),
};

const transactionSources: Enum[] = [
    { id: -1, title: "همه منابع" },
    { id: 0, title: "کیف پول" },
    { id: 1, title: "درگاه" },
];

function getBadgeTypeByStateId(stateId: number): BadgeType {
    switch (stateId) {
        case 1:
        case 6:
        case 10:
            return "waiting";
        case 2:
        case 9:
            return "success";
        case 3:
            return "error";
        case 4:
        case 8:
        case 14:
            return "info";
        case 5:
        case 7:
        case 13:
            return "primary";
        default:
            return "secondary";
    }
}

function buildTransactionQuery(filters: TransactionFilterValues): string {
    const parameters = new URLSearchParams();

    if (filters.trackingCode)
        parameters.set("trackingCode", filters.trackingCode.trim());
    if (filters.fullName) parameters.set("fullName", filters.fullName.trim());
    if (filters.phoneNumber)
        parameters.set("phoneNumber", faNumToEnNum(filters.phoneNumber.trim()));
    if (filters.bankAccount)
        parameters.set("bankAccount", faNumToEnNum(filters.bankAccount.trim()));
    if (filters.txId) parameters.set("txId", filters.txId.trim());
    if (filters.paymentIdentifier)
        parameters.set("paymentIdentifier", filters.paymentIdentifier.trim());
    if (filters.creatorId)
        parameters.set("creatorId", filters.creatorId.trim());
    if (filters.walletAddress)
        parameters.set("walletAddress", filters.walletAddress.trim());
    if (filters.voucherCode)
        parameters.set("voucherCode", filters.voucherCode.trim());
    if (filters.voucherActivationCode)
        parameters.set(
            "voucherActivationCode",
            filters.voucherActivationCode.trim(),
        );
    if (filters.market) parameters.set("market", filters.market.trim());
    if (filters.minAmount)
        parameters.set("minAmount", faNumToEnNum(filters.minAmount.trim()));
    if (filters.maxAmount)
        parameters.set("maxAmount", faNumToEnNum(filters.maxAmount.trim()));
    if (filters.startDate)
        parameters.set("startDate", jFormat(filters.startDate));
    if (filters.endDate) parameters.set("endDate", jFormat(filters.endDate));
    if (filters.marketType?.id !== -1)
        parameters.set("marketType", String(filters.marketType?.id));
    if (filters.type?.id !== -1)
        parameters.set("type", String(filters.type?.id));
    if (filters.state?.id !== -1)
        parameters.set("state", String(filters.state?.id));
    if (filters.platform?.id !== -1)
        parameters.set("platform", String(filters.platform?.id));
    if (filters.depositCurrencyType?.id !== -1)
        parameters.set(
            "depositCurrencyType",
            String(filters.depositCurrencyType?.id),
        );
    if (filters.source && filters.source.id !== -1)
        parameters.set("isAutoGenerated", String(filters.source.id === 1));
    if (filters.sort !== undefined)
        parameters.set("sort", String(filters.sort));

    const query = parameters.toString();
    return query ? `?${query}` : "";
}

export const Transactions = () => {
    const [transactionsList, setTransactionsList] =
        React.useState<PaginationList<Transaction>>();
    const [transactionsCount, setTransactionsCount] =
        React.useState<TransactionCount>();
    const [cardLoading, setCardLoading] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState<Transaction>();
    const [pageSize, setPageSize] = React.useState(10);
    const [query, setQuery] = React.useState("");
    const [filterValues, setFilterValues] =
        React.useState<TransactionFilterValues>(initialFilters);
    const [transactionMarketTypes, setTransactionMarketTypes] = React.useState<
        Enum[]
    >([initialFilters.marketType!]);
    const [transactionStates, setTransactionStates] = React.useState<Enum[]>([
        initialFilters.state!,
    ]);
    const [transactionTypes, setTransactionTypes] = React.useState<Enum[]>([
        initialFilters.type!,
    ]);
    const [transactionPlatforms, setTransactionPlatforms] = React.useState<
        Enum[]
    >([initialFilters.platform]);
    const [depositCurrencyTypes, setDepositCurrencyTypes] = React.useState<
        Enum[]
    >([initialFilters.depositCurrencyType]);

    const filterHandle = React.useCallback(
        (filters: TransactionFilterValues = filterValues) => {
            const nextQuery = buildTransactionQuery(filters);
            setQuery(nextQuery);
            getAllTransactions(
                setCardLoading,
                setTransactionsList,
                1,
                pageSize,
                nextQuery,
                setTransactionsCount,
            );
        },
        [filterValues, pageSize],
    );

    const applyColumnSearch = React.useCallback(
        (patch: Record<string, any>) => {
            const nextFilters = {
                ...filterValues,
                ...patch,
            } as TransactionFilterValues;
            setFilterValues(nextFilters);
            filterHandle(nextFilters);
        },
        [filterHandle, filterValues],
    );

    React.useEffect(() => {
        const loadInitialData = async () => {
            // این‌ها فقط گزینه‌های فیلتر را پر می‌کنند؛ اگر setCardLoading را به آن‌ها
            // بدهیم، چون سریع‌تر از کوئری اصلیِ جدول برمی‌گردند اسپینر را زودتر از
            // موعد خاموش می‌کنند و عملاً جدول بدون لودینگ دیده می‌شود.
            const ignoreLoading = () => {};
            await getMarketTypes(ignoreLoading, setTransactionMarketTypes);
            await getTransactionStates(ignoreLoading, setTransactionStates);
            await getTransactionPlatforms(
                ignoreLoading,
                setTransactionPlatforms,
            );
            await getDepositCurrencyType(
                ignoreLoading,
                setDepositCurrencyTypes,
            );
            await getTransactionTypes(ignoreLoading, setTransactionTypes);

            const initialQuery = buildTransactionQuery(initialFilters);
            setQuery(initialQuery);
            getAllTransactions(
                setCardLoading,
                setTransactionsList,
                1,
                10,
                initialQuery,
                setTransactionsCount,
            );
        };

        loadInitialData();
    }, []);

    // بعد از ویرایش موفق، جدول با همان فیلترها و همان صفحه دوباره گرفته می‌شود
    // تا کاربر جای خودش را در لیست از دست ندهد.
    const reloadTransactions = React.useCallback(() => {
        getAllTransactions(
            setCardLoading,
            setTransactionsList,
            transactionsCount?.pageIndex ?? 1,
            pageSize,
            query,
            setTransactionsCount,
        );
    }, [transactionsCount?.pageIndex, pageSize, query]);

    const handleTableChange = React.useCallback(
        (
            pagination: TablePaginationConfig,
            _filters: unknown,
            sorter: SorterResult<Transaction> | SorterResult<Transaction>[],
        ) => {
            const nextPage = pagination.current ?? 1;
            const nextPageSize = pagination.pageSize ?? pageSize;
            const currentPage = transactionsCount?.pageIndex ?? 1;
            const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            const nextSort =
                activeSorter?.columnKey === "createdAt"
                    ? activeSorter.order === "ascend"
                        ? SortDirection.ASC
                        : activeSorter.order === "descend"
                          ? SortDirection.DESC
                          : undefined
                    : filterValues.sort;
            const sortChanged = nextSort !== filterValues.sort;
            const nextFilters = sortChanged
                ? { ...filterValues, sort: nextSort }
                : filterValues;
            const nextQuery = sortChanged
                ? buildTransactionQuery(nextFilters)
                : query;
            const requestedPage = sortChanged ? 1 : nextPage;

            if (
                !sortChanged &&
                nextPage === currentPage &&
                nextPageSize === pageSize
            )
                return;

            setPageSize(nextPageSize);
            if (sortChanged) {
                setFilterValues(nextFilters);
                setQuery(nextQuery);
            }
            getAllTransactions(
                setCardLoading,
                setTransactionsList,
                requestedPage,
                nextPageSize,
                nextQuery,
                setTransactionsCount,
            );
        },
        [filterValues, pageSize, query, transactionsCount?.pageIndex],
    );

    const columns = React.useMemo<ColumnsType<Transaction>>(
        () => [
            {
                title: "#",
                key: "index",
                width: 70,
                render: (_, __, index) =>
                    ((transactionsCount?.pageIndex ?? 1) - 1) * pageSize +
                    index +
                    1,
            },
            {
                title: (
                    <ColumnSearch
                        title="شناسه"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "trackingCode",
                                placeholder: "شناسه تراکنش",
                                dir: "ltr",
                            },
                            {
                                key: "txId",
                                placeholder: "تی‌ایکس آیدی (TxId)",
                                dir: "ltr",
                            },
                            {
                                key: "walletAddress",
                                placeholder: "آدرس کیف پول",
                                dir: "ltr",
                            },
                            {
                                key: "voucherCode",
                                placeholder: "کد ووچر",
                                dir: "ltr",
                            },
                            {
                                key: "voucherActivationCode",
                                placeholder: "کد فعال‌سازی ووچر",
                                dir: "ltr",
                            },
                            {
                                key: "paymentIdentifier",
                                placeholder: "شناسه پرداخت",
                                dir: "ltr",
                            },
                        ]}
                    />
                ),
                dataIndex: "trackingCode",
                key: "trackingCode",
                width: 150,
                render: (trackingCode: string) => (
                    <CopyableText value={trackingCode} label="شناسه تراکنش" />
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="ارز"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "marketType",
                                placeholder: "نوع ارز",
                                type: "select",
                                options: transactionMarketTypes,
                            },
                            {
                                key: "market",
                                placeholder: "نام یا نماد ارز",
                                dir: "ltr",
                            },
                        ]}
                    />
                ),
                dataIndex: "market",
                key: "market",
                width: 135,
                render: (market: Transaction["market"]) => (
                    <span className="market-cell">
                        <img src={getIconPath(market.symbol)} alt="" />
                        <span className="en">{market.symbol}</span>
                    </span>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="نوع"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "type",
                                placeholder: "نوع تراکنش",
                                type: "select",
                                options: transactionTypes,
                            },
                            {
                                key: "depositCurrencyType",
                                placeholder: "نوع واریز",
                                type: "select",
                                options: depositCurrencyTypes,
                            },
                        ]}
                    />
                ),
                dataIndex: "type",
                key: "type",
                width: 135,
                render: (type: Enum) => (
                    <Tag
                        color={
                            type.id === TransactionType.Deposit
                                ? "green"
                                : "red"
                        }
                    >
                        {type.title}
                    </Tag>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="مقدار"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "minAmount",
                                placeholder: "حداقل مبلغ",
                                dir: "ltr",
                                htmlType: "number",
                            },
                            {
                                key: "maxAmount",
                                placeholder: "حداکثر مبلغ",
                                dir: "ltr",
                                htmlType: "number",
                            },
                        ]}
                    />
                ),
                dataIndex: "amount",
                key: "amount",
                align: "left",
                width: 145,
                render: (amount: number, row) => (
                    <div className="amount-cell">
                        <Popover title="مقدار">{digitSperator(amount)}</Popover>
                        {row.type.id === TransactionType.Withdrawal &&
                            row.market.symbol === "IRT" && (
                                <small>
                                    تسویه: {digitSperator(row.payoutAmount)}
                                </small>
                            )}
                    </div>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="کاربر"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "fullName",
                                placeholder: "نام و نام خانوادگی",
                            },
                            {
                                key: "phoneNumber",
                                placeholder: "شماره موبایل",
                                dir: "ltr",
                            },
                            {
                                key: "bankAccount",
                                placeholder: "شماره کارت / شبا",
                                dir: "ltr",
                            },
                            {
                                key: "creatorId",
                                placeholder: "شناسه کاربر",
                                dir: "ltr",
                            },
                        ]}
                    />
                ),
                key: "createdBy",
                width: 210,
                render: (_, row) => (
                    <div className="customer-cell">
                        <UserLink userId={row.userId}>
                            <span>{row.createdBy}</span>
                        </UserLink>
                        <small>
                            <CopyablePhone phoneNumber={row.phoneNumber} />
                        </small>
                    </div>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="وضعیت"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "state",
                                placeholder: "وضعیت",
                                type: "select",
                                options: transactionStates,
                            },
                        ]}
                    />
                ),
                dataIndex: "state",
                key: "state",
                width: 150,
                render: (state: Enum) => (
                    <Badge
                        text={state.title}
                        type={getBadgeTypeByStateId(state.id)}
                    />
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="منبع"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "source",
                                placeholder: "منبع تراکنش",
                                type: "select",
                                options: transactionSources,
                            },
                        ]}
                    />
                ),
                dataIndex: "isAutoGenerated",
                key: "isAutoGenerated",
                width: 110,
                render: (isAutoGenerated: boolean) => (
                    <Tag color={isAutoGenerated ? "purple" : "gold"}>
                        {isAutoGenerated ? "درگاه" : "کیف پول"}
                    </Tag>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="زمان"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "startDate",
                                placeholder: "از تاریخ",
                                type: "date",
                            },
                            {
                                key: "endDate",
                                placeholder: "تا تاریخ",
                                type: "date",
                            },
                        ]}
                    />
                ),
                dataIndex: "createdAt",
                key: "createdAt",
                width: 165,
                sorter: true,
                sortDirections: ["descend", "ascend", "descend"],
                sortOrder:
                    filterValues.sort === SortDirection.ASC
                        ? "ascend"
                        : filterValues.sort === SortDirection.DESC
                          ? "descend"
                          : undefined,
                render: (createdAt: string) => (
                    <span>
                        {moment.utc(createdAt).local().format("HH:mm")} {" - "}{" "}
                        {moment
                            .utc(createdAt)
                            .locale("fa")
                            .local()
                            .format("jYYYY/MM/DD")}
                    </span>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="پلتفرم"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "platform",
                                placeholder: "پلتفرم",
                                type: "select",
                                options: transactionPlatforms,
                            },
                        ]}
                    />
                ),
                dataIndex: "platform",
                key: "platform",
                width: 125,
                render: (platform: Enum) => (
                    <Tag color={platform?.id === 1 ? "blue" : "green"}>
                        {platform?.title}
                    </Tag>
                ),
            },
            {
                title: "عملیات",
                key: "actions",
                fixed: "left",
                width: 110,
                render: (_, row) => (
                    <div className="table-actions">
                        <Tooltip title="ویرایش تراکنش">
                            <button
                                type="button"
                                aria-label="ویرایش تراکنش"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setIsEditModalOpen(true);
                                }}
                            >
                                <EditOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="جزئیات تراکنش">
                            <button
                                type="button"
                                aria-label="جزئیات تراکنش"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setIsOpen(true);
                                }}
                            >
                                <InfoCircleOutlined />
                            </button>
                        </Tooltip>
                    </div>
                ),
            },
        ],
        [
            applyColumnSearch,
            depositCurrencyTypes,
            filterValues,
            pageSize,
            transactionMarketTypes,
            transactionPlatforms,
            transactionStates,
            transactionTypes,
            transactionsCount?.pageIndex,
        ],
    );

    return (
        <Page title="مدیریت تراکنش‌ها" className="pmx-transactions">
            {isEditModalOpen && (
                <TransactionEditModal
                    transactionStates={transactionStates}
                    isOpen={isEditModalOpen}
                    setIsOpen={setIsEditModalOpen}
                    item={selectedItem}
                    onSuccess={reloadTransactions}
                />
            )}
            {/* اسپینر فقط روی خود جدول است؛ blur روی Card باعث دو اسپینر هم‌زمان می‌شد. */}
            <Card title="تراکنش‌ها">
                {selectedItem && (
                    <TransactionDetailModal
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        id={selectedItem.id}
                    />
                )}
                <div className="table-toolbar">
                    <Button
                        className="export-btn"
                        text="خروجی اکسل"
                        type={buttonType.success}
                        icon={<ExportOutlined />}
                        onClick={() =>
                            downloadCSV(
                                "تراکنش‌ها",
                                transactionsList?.items ?? [],
                            )
                        }
                    />
                </div>
                <AntdTable<Transaction>
                    className="transactions-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={transactionsList?.items ?? []}
                    loading={cardLoading}
                    locale={{
                        emptyText: <Empty description="رکوردی یافت نشد" />,
                    }}
                    scroll={{ x: 1610 }}
                    pagination={{
                        current: transactionsCount?.pageIndex ?? 1,
                        pageSize: transactionsCount?.pageSize ?? pageSize,
                        total: transactionsCount?.totalCount ?? 0,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100, 1000, 10000, 100000],
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                    onChange={handleTableChange}
                />
            </Card>
        </Page>
    );
};
